import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Alert, Box, Button, FormLabel, Checkbox, FormControl, TextField, Typography, Card as MuiCard, FormControlLabel, Paper } from '@mui/material';
import { Done } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../api/instance';
import logIn from '../../images/ui/log-in.svg'
import { OpalCard, paperStyles } from '../../components/OpalCard/OpalCard';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  marginTop: theme.spacing(8),
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: '3rem auto 0.5rem auto',
  borderRadius: '12px',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow: 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow: 'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const Login = () => {
  const navigate = useNavigate();
  const { setAuthenticated, setUser } = useAuth();
  const [submitting, setSubmitting] = useState();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();

  const validateInputs = () => {
    const email = credentials.email;
    const password = credentials.password;

    let isValid = true;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = async () => {
    const validInputs = validateInputs();
    if (validInputs) {
      const response = await api.post('/users/login', credentials);

      if (response.status === 200) {
        const { auth } = response.data;
        localStorage.setItem('auth', auth);
        const decoded = jwtDecode(auth);

        setUser(decoded);

        setSubmitting(false);
        setAuthenticated(true);
        navigate('/site-dash');
      } else {
        console.log('Could not login');
        setTimeout(() => {
          setSubmitting(false);
        }, 1000);
      }

      if (response.status === 401) {
        setErrorMessage('Unauthorized access');
      }

      if (response.status === 404) {
        setErrorMessage('This user does not exist');
      }
    }
  };

  const logoutStatus = location?.state?.logout;

  return (
    <>
      <Card variant="outlined">
        <OpalCard>
          <Paper elevation={0} sx={{ ...paperStyles, maxWidth: 800, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={logIn} alt='login' width="100" height="100" />
            </div>
            {logoutStatus &&
              <Alert sx={{ margin: '1rem 0' }} icon={<Done fontSize="inherit" />} severity="success">
                {logoutStatus}
              </Alert>
            }

            <Box
              noValidate
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: 2,
              }}
            >
              <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <TextField
                  error={emailError}
                  helperText={emailErrorMessage}
                  id="email"
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  color={emailError ? 'error' : 'primary'}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="password">Password</FormLabel>
                <TextField
                  error={passwordError}
                  helperText={passwordErrorMessage}
                  name="password"
                  placeholder="••••••"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  color={passwordError ? 'error' : 'primary'}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                />
              </FormControl>
              <FormControlLabel control={<Checkbox value="remember" color="primary" />} label="Remember me" />
              <Typography color="error">
                {errorMessage}
                {submitting && 'Processing credentials'}
              </Typography>
              <Button type="submit" fullWidth variant="contained" onClick={handleSubmit}>
                Sign in
              </Button>
            </Box>
          </Paper>
        </OpalCard>
      </Card>
      <Typography variant="body2" color="textSecondary" align="center" sx={{ fontSize: 10 }}>
        &copy;  EFX PRO Media - {new Date().getFullYear()} - All Rights Reserved - Elements dashboard version (Beta)
      </Typography>
    </>
  );
};

export default Login;
